const CLIENT_ID = process.env.REACT_APP_AAD_CLIENT_ID || '';
const TENANT = 'chkcoao.onmicrosoft.com';
const REDIRECT_URI = window.location.origin + '/auth.html';
const SCOPES = ['Sites.ReadWrite.All', 'User.Read', 'ChannelMessage.Send', 'GroupMember.Read.All', 'offline_access'];

function base64url(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generatePKCE() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  const verifier = base64url(arr.buffer as ArrayBuffer);
  const encoded = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', encoded);
  const challenge = base64url(hash);
  return { verifier, challenge };
}

function storeToken(token: string, expiry: number) {
  sessionStorage.setItem('access_token', token);
  sessionStorage.setItem('token_expiry', String(expiry));
}

export function getCachedToken(): string | null {
  const token = sessionStorage.getItem('access_token');
  const expiry = Number(sessionStorage.getItem('token_expiry') || 0);
  if (token && Date.now() < expiry - 60000) return token;
  return null;
}

export async function loginWithPopup(): Promise<string> {
  const { verifier, challenge } = await generatePKCE();
  sessionStorage.setItem('pkce_verifier', verifier);

  const state = Math.random().toString(36).substring(2);
  sessionStorage.setItem('oauth_state', state);

  const authUrl =
    `https://login.microsoftonline.com/${TENANT}/oauth2/v2.0/authorize` +
    `?client_id=${CLIENT_ID}` +
    `&response_type=code` +
    `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
    `&scope=${encodeURIComponent(SCOPES.join(' '))}` +
    `&response_mode=query` +
    `&code_challenge=${challenge}` +
    `&code_challenge_method=S256` +
    `&state=${state}`;

  return new Promise((resolve, reject) => {
    const popup = window.open(authUrl, 'auth', 'width=520,height=640');
    if (!popup) { reject(new Error('Popup bloqueado')); return; }

    const handler = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (!e.data?.code) return;
      window.removeEventListener('message', handler);

      try {
        const params = new URLSearchParams();
        params.append('client_id', CLIENT_ID);
        params.append('grant_type', 'authorization_code');
        params.append('code', e.data.code);
        params.append('redirect_uri', REDIRECT_URI);
        params.append('code_verifier', sessionStorage.getItem('pkce_verifier') || '');
        params.append('scope', SCOPES.join(' '));

        const resp = await fetch(
          `https://login.microsoftonline.com/${TENANT}/oauth2/v2.0/token`,
          { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: params }
        );
        const data = await resp.json();
        if (data.error) throw new Error(data.error_description || data.error);

        storeToken(data.access_token, Date.now() + data.expires_in * 1000);
        if (data.refresh_token) sessionStorage.setItem('refresh_token', data.refresh_token);
        resolve(data.access_token);
      } catch (err) {
        reject(err);
      }
    };

    window.addEventListener('message', handler);

    // Timeout
    setTimeout(() => {
      window.removeEventListener('message', handler);
      if (!popup.closed) popup.close();
      reject(new Error('Timeout de autenticação'));
    }, 120000);
  });
}

export async function refreshAccessToken(): Promise<string | null> {
  const rt = sessionStorage.getItem('refresh_token');
  if (!rt) return null;
  const params = new URLSearchParams();
  params.append('client_id', CLIENT_ID);
  params.append('grant_type', 'refresh_token');
  params.append('refresh_token', rt);
  params.append('scope', SCOPES.join(' '));
  const resp = await fetch(
    `https://login.microsoftonline.com/${TENANT}/oauth2/v2.0/token`,
    { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: params }
  );
  const data = await resp.json();
  if (data.error) return null;
  storeToken(data.access_token, Date.now() + data.expires_in * 1000);
  if (data.refresh_token) sessionStorage.setItem('refresh_token', data.refresh_token);
  return data.access_token;
}
